import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Box,
  Typography,
} from "@mui/material";
import {
  getBatchSubjectList,
  getChapterList,
  getSubTopicList,
} from "utils/metadata-split/metaDataApiFetch";

const QUESTION_TYPE_OPTIONS = [
  "DESCRIPTIVE",
  "FILL_IN_BLANKS",
  "MULTI_SELECT",
  "SINGLE_SELECT",
  "UNKNOWN",
];

const NODE_TYPE_OPTIONS = ["MainQuestion", "GroupQuestion", "SubQuestion"];

type QuestionData = {
  id?: number;
  questionNumber?: string;
  questionType?: string;
  nodeType?: string;
  marks?: number | string;
  choiceCount?: number | string;
  subjectId?: string;
  subjectName?: string;
  chapterId?: string;
  chapterName?: string;
  topicId?: string;
  topicName?: string;
  createdByName?: string;
  updatedByName?: string;
  createdAt?: string;
  updatedAt?: string;
};

type Props = {
  index: number;
  data: QuestionData;
  onChange: (newData: QuestionData) => void;
};

function QuestionForm({ index, data, onChange }: Props) {
  const [subjects, setSubjects] = useState<any[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [topics, setTopics] = useState<any[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);

  useEffect(() => {
    if (data.subjectName && !data.subjectId && subjects.length > 0) {
      const matchedSubject = subjects.find(
        (sub: any) => sub.subject_name === data.subjectName
      );
      if (matchedSubject) {
        handleChange({ subjectId: String(matchedSubject.subject_id) });
      }
    }
    if (data.chapterName && !data.chapterId && chapters.length > 0) {
      const matchedChapter = chapters.find(
        (ch: any) => ch.chapter_name === data.chapterName
      );
      if (matchedChapter) {
        handleChange({ chapterId: String(matchedChapter.chapter_id) });
      }
    }
    if (data.topicName && !data.topicId && topics.length > 0) {
      const matchedTopic = topics.find(
        (tp: any) => tp.topic_name === data.topicName
      );
      if (matchedTopic) {
        handleChange({ topicId: String(matchedTopic.topic_id) });
      }
    }
  }, [data, subjects, chapters, topics]);

  const handleChange = (updates: Partial<QuestionData>) => {
    onChange({ ...data, ...updates });
  };

  // Fetch subjects on mount and when needed
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setLoadingSubjects(true);
        const res: any = await getBatchSubjectList();
        if (res && Array.isArray(res[0])) {
          setSubjects(res[0]);
        }
      } catch (error) {
        console.error("Error fetching subjects", error);
      } finally {
        setLoadingSubjects(false);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (!data.subjectId) {
      setChapters([]);
      return;
    }

    const fetchChapters = async () => {
      try {
        setLoadingChapters(true);
        const params: any = {
          batch_id: process.env.REACT_APP_GENERIC_BATCH_ID,
          subject_id: data.subjectId,
        };
        const res: any = await getChapterList(params);
        if (res && Array.isArray(res[0])) {
          setChapters(res[0]);
        }
      } catch (error) {
        console.error("Error fetching chapters", error);
      } finally {
        setLoadingChapters(false);
      }
    };
    fetchChapters();
  }, [data.subjectId]);

  useEffect(() => {
    if (!data.chapterId) {
      setTopics([]);
      return;
    }

    const fetchTopics = async () => {
      try {
        setLoadingTopics(true);
        const params: any = {
          batch_id: process.env.REACT_APP_GENERIC_BATCH_ID,
          subject_id: data.subjectId,
          chapter_id: data.chapterId,
        };
        const res: any = await getSubTopicList(params);
        if (res && Array.isArray(res[0])) {
          setTopics(res[0]);
        }
      } catch (error) {
        console.error("Error fetching topics", error);
      } finally {
        setLoadingTopics(false);
      }
    };
    fetchTopics();
  }, [data.chapterId]);

  const handleSubjectChange = (subjectId: string) => {
    const selectedSubject = subjects.find(
      (sub: any) => String(sub.subject_id) === subjectId
    );

    handleChange({
      subjectId: subjectId,
      subjectName: selectedSubject?.subject_name || "",
      chapterId: "",
      chapterName: "",
      topicId: "",
      topicName: "",
    });
  };

  const handleChapterChange = (chapterId: string) => {
    const selectedChapter = chapters.find(
      (ch: any) => String(ch.chapter_id) === chapterId
    );

    handleChange({
      chapterId: chapterId,
      chapterName: selectedChapter?.chapter_name || "",
      topicId: "",
      topicName: "",
    });
  };

  const handleTopicChange = (topicId: string) => {
    const selectedTopic = topics.find(
      (tp: any) => String(tp.topic_id) === topicId
    );

    handleChange({
      topicId: topicId,
      topicName: selectedTopic?.topic_name || "",
    });
  };

  console.log("data.nodeType", data);
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#fafafa",
        borderRadius: 2,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ mb: 2, color: "text.secondary", fontWeight: "medium" }}
      >
        Question Details
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Question Number"
            size="small"
            fullWidth
            value={data.questionNumber || ""}
            onChange={(e) => handleChange({ questionNumber: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Question Type</InputLabel>
            <Select
              value={data.questionType || ""}
              onChange={(e) => handleChange({ questionType: e.target.value })}
              label="Question Type"
            >
              <MenuItem value="">Select Type</MenuItem>
              {QUESTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Node Type</InputLabel>
            <Select
              value={data.nodeType || ""}
              onChange={(e) => handleChange({ nodeType: e.target.value })}
              label="Node Type"
              disabled
            >
              <MenuItem value="">Select Node Type</MenuItem>
              {NODE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Marks"
            type="number"
            size="small"
            fullWidth
            value={data.marks ?? ""}
            onChange={(e) =>
              handleChange({
                marks: e.target.value ? Number(e.target.value) : "",
              })
            }
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Choice Count"
            type="number"
            size="small"
            fullWidth
            value={data.choiceCount ?? ""}
            onChange={(e) =>
              handleChange({
                choiceCount: e.target.value ? Number(e.target.value) : "",
              })
            }
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Subject</InputLabel>
            <Select
              value={data.subjectId || ""}
              onChange={(e) => handleSubjectChange(e.target.value)}
              label="Subject"
            >
              <MenuItem value="">Select Subject</MenuItem>
              {loadingSubjects ? (
                <MenuItem disabled>
                  <CircularProgress size={18} /> Loading...
                </MenuItem>
              ) : (
                subjects.map((sub: any) => (
                  <MenuItem key={sub.subject_id} value={String(sub.subject_id)}>
                    {sub.subject_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Chapter</InputLabel>
            <Select
              value={data.chapterId || ""}
              onChange={(e) => handleChapterChange(e.target.value)}
              label="Chapter"
              disabled={!data.subjectId}
            >
              <MenuItem value="">Select Chapter</MenuItem>
              {loadingChapters ? (
                <MenuItem disabled>
                  <CircularProgress size={18} /> Loading...
                </MenuItem>
              ) : (
                chapters.map((ch: any) => (
                  <MenuItem key={ch.chapter_id} value={String(ch.chapter_id)}>
                    {ch.chapter_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Topic</InputLabel>
            <Select
              value={data.topicId || ""}
              onChange={(e) => handleTopicChange(e.target.value)}
              label="Topic"
              disabled={!data.chapterId}
            >
              <MenuItem value="">Select Topic</MenuItem>
              {loadingTopics ? (
                <MenuItem disabled>
                  <CircularProgress size={18} /> Loading...
                </MenuItem>
              ) : (
                topics.map((tp: any) => (
                  <MenuItem key={tp.topic_id} value={String(tp.topic_id)}>
                    {tp.topic_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

export default QuestionForm;
